import React from 'react';
import { StyleSheet, ViewStyle } from 'react-native';
import { Pressable, PressableProps } from '../primitives/Pressable';
import { Text } from '../primitives/Text';
import { Spinner } from './Spinner';

export interface ButtonProps extends PressableProps {
    label?: string;
    children?: React.ReactNode;
    className?: string; // Still accepted for web-compatibility but not used in native
}

/**
 * Universal Button Component
 * High-end, multi-platform button that abstracts complexity between web and mobile.
 */
export const Button: React.FC<ButtonProps> = ({
    label,
    children,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    disabled,
    style,
    onPress,
    ...props
}) => {
    const isGhost = variant === 'ghost';

    const getBackgroundColor = () => {
        if (disabled || isLoading) return '#94a3b8'; // neutral-400
        switch (variant) {
            case 'primary': return '#16a34a'; // primary-600
            case 'secondary': return '#CBD5E1'; // neutral-300 / secondary-200 equivalent
            case 'danger': return '#ef4444'; // danger
            case 'ghost': return 'transparent';
            default: return 'transparent';
        }
    };

    const getPadding = () => {
        switch (size) {
            case 'sm': return { paddingVertical: 4, paddingHorizontal: 8 };
            case 'lg': return { paddingVertical: 12, paddingHorizontal: 24 };
            default: return { paddingVertical: 8, paddingHorizontal: 16 };
        }
    };

    const textColor = isGhost ? '#16a34a' : (variant === 'secondary' ? '#1E293B' : '#ffffff');

    return (
        <Pressable
            onPress={onPress}
            disabled={disabled || isLoading}
            style={[
                styles.button,
                {
                    backgroundColor: getBackgroundColor(),
                    borderRadius: 6,
                },
                getPadding(),
                style as ViewStyle,
            ]}
            {...props}
        >
            {isLoading ? (
                <Spinner size="sm" color={textColor} />
            ) : (
                <>
                    {children ? children : (
                        <Text
                            variant="button"
                            color={textColor}
                            weight="600"
                            align="center"
                        >
                            {label || ''}
                        </Text>
                    )}
                </>
            )}
        </Pressable>
    );
};

const styles = StyleSheet.create({
    button: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: 80,
    },
});
